import React, { useEffect, useState, useCallback } from 'react';
import { Box, Typography, Button, Container, Paper, CircularProgress } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { verifyEmail, resendVerification } from '../api/auth';

// Публичная страница — переход по ссылке из письма подтверждения email
// (см. auth-user-services/src/services/emailVerification.js buildLink()).
const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const uid = searchParams.get('uid');

  const [status, setStatus] = useState('pending'); // pending | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [returnUrl, setReturnUrl] = useState(null);
  const [resendEmail, setResendEmail] = useState('');
  const [isResending, setIsResending] = useState(false);

  const runVerification = useCallback(async () => {
    if (!token || !uid) {
      setStatus('error');
      setErrorMessage('This verification link is missing required parameters.');
      return;
    }

    try {
      const result = await verifyEmail(token, uid);
      setReturnUrl(result?.returnUrl || null);
      setStatus('success');
    } catch (error) {
      setStatus('error');
      setErrorMessage(
        error.response?.data?.message || 'This verification link is invalid or has expired.'
      );
    }
  }, [token, uid]);

  useEffect(() => {
    runVerification();
  }, [runVerification]);

  const handleResend = async () => {
    if (!resendEmail) {
      toast.error('Enter your email address first');
      return;
    }
    setIsResending(true);
    try {
      await resendVerification(resendEmail);
      toast.success('If this account needs verification, a new email has been sent.');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to resend verification email');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          textAlign: 'center',
        }}
      >
        <Paper elevation={3} sx={{ p: 4, borderRadius: 2, maxWidth: 420, width: '100%' }}>
          {status === 'pending' && (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography variant="h6">Verifying your email…</Typography>
            </>
          )}

          {status === 'success' && (
            <>
              <Typography variant="h4" component="h1" gutterBottom color="success.main">
                Email confirmed
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                Your email address has been verified. You can now sign in.
              </Typography>
              {returnUrl ? (
                <>
                  {/* returnUrl — куда пришла регистрация (anyemp.com и т.п.), см.
                      auth-user-services/src/services/auth.js metadata.registrationDomain.
                      Голый href, не navigate(): это другой origin, не роут этого SPA. */}
                  <Button variant="contained" color="primary" fullWidth component="a" href={returnUrl}>
                    Return to {new URL(returnUrl).hostname.replace(/^www\./, '')}
                  </Button>
                  <Button variant="text" fullWidth sx={{ mt: 1 }} onClick={() => navigate('/login')}>
                    Or sign in here
                  </Button>
                </>
              ) : (
                <Button variant="contained" color="primary" fullWidth onClick={() => navigate('/login')}>
                  Go to sign in
                </Button>
              )}
            </>
          )}

          {status === 'error' && (
            <>
              <Typography variant="h4" component="h1" gutterBottom color="error.main">
                Verification failed
              </Typography>
              <Typography variant="body1" sx={{ mb: 3, color: 'text.secondary' }}>
                {errorMessage}
              </Typography>

              <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                Request a new verification link:
              </Typography>
              <Box
                component="input"
                type="email"
                placeholder="you@example.com"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                sx={{
                  width: '100%',
                  p: 1.25,
                  mb: 2,
                  borderRadius: 1,
                  border: '1px solid rgba(0,0,0,0.23)',
                  fontSize: '0.95rem',
                }}
              />
              <Button
                variant="contained"
                color="primary"
                fullWidth
                disabled={isResending}
                onClick={handleResend}
                sx={{ mb: 2 }}
              >
                {isResending ? <CircularProgress size={22} /> : 'Resend verification email'}
              </Button>
              <Button variant="text" fullWidth onClick={() => navigate('/login')}>
                Back to sign in
              </Button>
            </>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default VerifyEmail;
